// Login próprio com Google e Microsoft (US-048, AC-097, AC-099): o app abre direto o login do
// provedor pedindo um token de identidade e o entrega ao Supabase (signInWithIdToken). Assim a
// tela do provedor mostra beedoit.app, e não o endereço do projeto no Supabase.
// O `state` amarra o retorno ao pedido; o nonce vai ao provedor em SHA-256 e o original fica só
// aqui — um token interceptado no caminho não serve sem ele.
import { AUTH_CALLBACK_URL, type OAuthProvider } from '../data/auth';

/**
 * IDs dos clientes OAuth, do .env e dos segredos do GitHub no release (ASM-128). O segredo do
 * cliente fica só no Supabase. Provedor sem ID usa o fluxo antigo pelo Supabase.
 */
export function readOAuthClientIds(env: Record<string, string | undefined> = import.meta.env): Partial<Record<OAuthProvider, string>> {
  const ids: Partial<Record<OAuthProvider, string>> = {};
  const google = env.VITE_GOOGLE_CLIENT_ID?.trim();
  const microsoft = env.VITE_MICROSOFT_CLIENT_ID?.trim();
  if (google) ids.google = google;
  if (microsoft) ids.azure = microsoft;
  return ids;
}

/**
 * Botões de login social na tela (AC-240): o Google sempre (ID próprio ou o fluxo pelo Supabase);
 * a Microsoft só com o ID do cliente configurado — sem ele, o provedor ainda não foi ativado (Q-042).
 */
export function visibleOAuthProviders(ids: Partial<Record<OAuthProvider, string>>): OAuthProvider[] {
  return ids.azure ? ['google', 'azure'] : ['google'];
}

/** Página do site que devolve o login ao app desktop pelo beedoit:// (AC-098). */
export const SITE_AUTH_CALLBACK_URL = 'https://beedoit.app/auth/callback/';

const AUTHORIZE_URL: Record<OAuthProvider, string> = {
  google: 'https://accounts.google.com/o/oauth2/v2/auth',
  azure: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
};

export interface PendingLogin {
  provider: OAuthProvider;
  state: string;
  nonce: string;
}

/** Onde o pedido em andamento espera a volta do provedor. */
export interface PendingStore {
  get(): PendingLogin | null;
  set(p: PendingLogin | null): void;
}

/** Desktop: o app continua aberto enquanto o navegador faz o login (single-instance). */
export function memoryPending(): PendingStore {
  let current: PendingLogin | null = null;
  return { get: () => current, set: (p) => void (current = p) };
}

const PENDING_KEY = 'bee-oauth-pending';

/** Web: a aba sai para o provedor e volta, então o pedido fica no sessionStorage da aba. */
export function sessionPending(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>): PendingStore {
  return {
    get() {
      try {
        return JSON.parse(storage.getItem(PENDING_KEY) ?? 'null') as PendingLogin | null;
      } catch {
        return null;
      }
    },
    set(p) {
      if (p) storage.setItem(PENDING_KEY, JSON.stringify(p));
      else storage.removeItem(PENDING_KEY);
    },
  };
}

export function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface IdTokenLoginOptions {
  clientIds: Partial<Record<OAuthProvider, string>>;
  redirectUri: string;
  pending: PendingStore;
  /** Desktop: abre o navegador do sistema. Web: leva a aba para o provedor. */
  navigate(url: string): void | Promise<void>;
}

/**
 * Começa o login pelo provedor. Devolve false quando o provedor não tem ID de cliente no app —
 * aí vale o fluxo antigo pelo Supabase (AC-085, AC-090).
 */
export function createIdTokenLogin(opts: IdTokenLoginOptions) {
  return async (provider: OAuthProvider): Promise<boolean> => {
    const clientId = opts.clientIds[provider];
    if (!clientId) return false;
    const state = randomToken();
    const nonce = randomToken();
    opts.pending.set({ provider, state, nonce });
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: opts.redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce: await sha256Hex(nonce),
      state,
      prompt: 'select_account',
    });
    if (provider === 'azure') params.set('response_mode', 'fragment');
    await opts.navigate(`${AUTHORIZE_URL[provider]}?${params}`);
    return true;
  };
}

/** Lê o token ou o erro do retorno (fragmento ou query); null se não for um retorno de login por token. */
export function parseIdTokenCallback(url: string): { idToken: string; state: string } | { error: string } | null {
  const hashAt = url.indexOf('#');
  const queryAt = url.indexOf('?');
  const raw = hashAt >= 0 ? url.slice(hashAt + 1) : queryAt >= 0 ? url.slice(queryAt + 1) : '';
  const params = new URLSearchParams(raw);
  const idToken = params.get('id_token');
  const state = params.get('state');
  if (idToken && state) return { idToken, state };
  const error = params.get('error');
  if (error && state) return { error: params.get('error_description') ?? error };
  return null;
}

export interface IdTokenAuthClient {
  signInWithIdToken(args: { provider: OAuthProvider; token: string; nonce: string }): Promise<{ error: unknown }>;
}

/** Fecha o login: confere o `state` e troca o token pela sessão do Supabase. */
export async function completeIdTokenLogin(url: string, pending: PendingStore, auth: IdTokenAuthClient): Promise<boolean> {
  const parsed = parseIdTokenCallback(url);
  if (!parsed) return false;
  const p = pending.get();
  if ('error' in parsed) {
    pending.set(null);
    return false;
  }
  if (!p || p.state !== parsed.state) return false;
  pending.set(null);
  const { error } = await auth.signInWithIdToken({ provider: p.provider, token: parsed.idToken, nonce: p.nonce });
  return !error;
}

/** Desktop: o link beedoit://auth/callback#id_token=… que a página do site abre. */
export function completeDesktopIdTokenLogin(url: string, pending: PendingStore, auth: IdTokenAuthClient): Promise<boolean> {
  if (!url.startsWith(AUTH_CALLBACK_URL)) return Promise.resolve(false);
  return completeIdTokenLogin(url, pending, auth);
}

export interface BrowserWindowLike {
  location: { href: string; hash: string; pathname: string; search: string };
  history: { replaceState(data: unknown, unused: string, url: string): void };
}

/** Web: ao abrir o app de volta do provedor, cria a sessão e tira o token da barra de endereços. */
export async function finishWebIdTokenLogin(win: BrowserWindowLike, pending: PendingStore, auth: IdTokenAuthClient): Promise<boolean> {
  if (!parseIdTokenCallback(win.location.hash)) return false;
  const url = win.location.href;
  win.history.replaceState(null, '', win.location.pathname + win.location.search);
  return completeIdTokenLogin(url, pending, auth);
}
